import { useState } from 'react'
import { createPortal } from 'react-dom'
import ConfirmDialog from './ConfirmDialog'
import TagSelector from './TagSelector'
import { Tag } from '../api/notes'
import apiClient from '../api/client'

interface BulkActionsProps {
  selectedIds: string[]
  onClearSelection: () => void
  onActionComplete: () => void
  currentStatus?: string // 'notes' | 'starred' | 'archive' | 'removed'
}

type ActionType = 'star' | 'unstar' | 'home' | 'unhome' | 'archive' | 'unarchive' | 'delete' | 'restore' | 'permanentDelete' | 'tags'

export default function BulkActions({
  selectedIds,
  onClearSelection,
  onActionComplete,
  currentStatus = 'notes',
}: BulkActionsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [confirmAction, setConfirmAction] = useState<ActionType | null>(null)
  const [showTagSelector, setShowTagSelector] = useState(false)
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])

  const count = selectedIds.length

  if (count === 0) return null

  const performAction = async (action: ActionType) => {
    setIsLoading(true)
    try {
      switch (action) {
        case 'star':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_starred: true },
          })
          break
        case 'unstar':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_starred: false },
          })
          break
        case 'home':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_home: true },
          })
          break
        case 'unhome':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_home: false },
          })
          break
        case 'archive':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_archived: true },
          })
          break
        case 'unarchive':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_archived: false },
          })
          break
        case 'delete':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_thrashed: true, thrashed_time: Math.floor(Date.now() / 1000) },
          })
          break
        case 'restore':
          await apiClient.put('/notes/partial_update_note', {
            note_ids: selectedIds,
            note_partial: { is_thrashed: false, thrashed_time: null },
          })
          break
        case 'permanentDelete':
          await apiClient.put('/notes/delete', { note_ids: selectedIds })
          break
        case 'tags':
          if (selectedTags.length > 0) {
            await apiClient.put('/notes/update_note_tags', {
              note_ids: selectedIds,
              tags: selectedTags.map(t => ({ id: t.id, name: t.name })),
            })
          }
          break
      }
      onClearSelection()
      onActionComplete()
    } catch (err) {
      console.error(`Failed to perform action ${action}:`, err)
    } finally {
      setIsLoading(false)
      setConfirmAction(null)
      setShowTagSelector(false)
      setSelectedTags([])
    }
  }

  const handleAction = (action: ActionType) => {
    // Actions that need confirmation
    if (['delete', 'permanentDelete'].includes(action)) {
      setConfirmAction(action)
    } else if (action === 'tags') {
      setShowTagSelector(true)
    } else {
      performAction(action)
    }
  }

  const getConfirmDialogProps = () => {
    switch (confirmAction) {
      case 'delete':
        return {
          title: 'Move to Trash',
          message: `Are you sure you want to move ${count} note${count > 1 ? 's' : ''} to trash?`,
          confirmLabel: 'Move to Trash',
          confirmVariant: 'danger' as const,
        }
      case 'permanentDelete':
        return {
          title: 'Permanently Delete',
          message: `This will permanently delete ${count} note${count > 1 ? 's' : ''}. This action cannot be undone.`,
          confirmLabel: 'Delete Forever',
          confirmVariant: 'danger' as const,
        }
      default:
        return {
          title: 'Confirm',
          message: 'Are you sure?',
          confirmLabel: 'Confirm',
          confirmVariant: 'primary' as const,
        }
    }
  }

  const isTrash = currentStatus === 'removed'
  const isArchive = currentStatus === 'archive'
  const isStarred = currentStatus === 'starred'

  return (
    <>
      {/* Bulk Actions Toolbar */}
      <div data-testid="bulk-actions-bar" className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-white border-2 border-primary rounded-xl shadow-xl px-4 py-3 flex items-center gap-3">
        {/* Selection count */}
        <div className="flex items-center gap-2 pr-3 border-r border-gray-200">
          <span className="text-sm font-medium text-gray-900">{count} selected</span>
          <button
            onClick={onClearSelection}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title="Clear selection"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {/* Star/Unstar */}
          {!isTrash && (
            <button
              onClick={() => handleAction(isStarred ? 'unstar' : 'star')}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:text-yellow-500 hover:bg-gray-100 rounded-lg transition-colors"
              title={isStarred ? 'Remove from starred' : 'Add to starred'}
            >
              <svg className="w-5 h-5" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          )}

          {/* Home */}
          {!isTrash && (
            <button
              onClick={() => handleAction('home')}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
              title="Add to home"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          )}

          {/* Tags */}
          {!isTrash && (
            <button
              onClick={() => handleAction('tags')}
              disabled={isLoading}
              data-testid="bulk-tags-btn"
              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
              title="Assign tags"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </button>
          )}

          {/* Archive/Unarchive */}
          {!isTrash && (
            <button
              onClick={() => handleAction(isArchive ? 'unarchive' : 'archive')}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
              title={isArchive ? 'Unarchive' : 'Archive'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </button>
          )}

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Restore (only in trash) */}
          {isTrash && (
            <button
              onClick={() => handleAction('restore')}
              disabled={isLoading}
              data-testid="bulk-restore-btn"
              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
              title="Restore"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}

          {/* Delete/Permanent Delete */}
          <button
            onClick={() => handleAction(isTrash ? 'permanentDelete' : 'delete')}
            disabled={isLoading}
            data-testid="bulk-delete-btn"
            className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title={isTrash ? 'Delete permanently' : 'Move to trash'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmAction !== null}
        onConfirm={() => confirmAction && performAction(confirmAction)}
        onCancel={() => setConfirmAction(null)}
        isLoading={isLoading}
        {...getConfirmDialogProps()}
      />

      {/* Tag Selector Dialog — rendered in a portal to escape z-index stacking context */}
      {showTagSelector && createPortal(
        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => { setShowTagSelector(false); setSelectedTags([]) }}
          />
          <div className="flex min-h-full items-center justify-center p-4 pointer-events-none">
            <div
              data-testid="bulk-tags-dialog"
              className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Assign Tags to {count} Note{count > 1 ? 's' : ''}
              </h3>
              <TagSelector
                selectedTags={selectedTags}
                onChange={setSelectedTags}
                placeholder="Select tags..."
              />
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowTagSelector(false)
                    setSelectedTags([])
                  }}
                  data-testid="bulk-tags-cancel"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => performAction('tags')}
                  disabled={isLoading || selectedTags.length === 0}
                  data-testid="bulk-tags-apply"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-secondary disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  Apply Tags
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
